import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import { sendUserInvitation } from '../../services/mutations';
import { cn } from '../../lib/utils';
import type { RoleType } from './useData';

const ROLE_OPTIONS: { value: RoleType; label: string }[] = [
  { value: 'USER', label: 'User' },
  { value: 'ADMIN', label: 'Admin' },
  { value: 'GUEST', label: 'Guest' },
  { value: 'CLIENT', label: 'Client' },
];

interface InviteUserModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const InviteUserModal: React.FC<InviteUserModalProps> = ({ open, onClose, onSuccess }) => {
  const { colors } = useTheme();
  const { showNotification } = useNotification();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<RoleType>('USER');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email.trim()) {
      setError('Email is required.');
      return;
    }
    setLoading(true);
    try {
      await sendUserInvitation({ email: email.trim(), role });
      showNotification?.('Invitation sent successfully.', 'success');
      setEmail('');
      setRole('USER');
      onClose();
      onSuccess?.();
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to send invitation.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      setEmail('');
      setRole('USER');
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={handleClose} aria-hidden="true" />
      <div
        className={cn(
          'relative z-10 w-full max-w-md rounded-lg shadow-xl p-6',
          colors.background,
          colors.border,
          'border'
        )}
      >
        <h2 className="text-lg font-semibold mb-4">Invite User</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm p-3">
              {error}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={cn(
                'w-full rounded-md border px-3 py-2 text-sm',
                colors.background,
                colors.border,
                'border'
              )}
              placeholder="user@example.com"
              autoFocus
              disabled={loading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as RoleType)}
              className={cn(
                'w-full rounded-md border px-3 py-2 text-sm',
                colors.background,
                colors.border,
                'border'
              )}
              disabled={loading}
            >
              {ROLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className={cn(
                'px-4 py-2 rounded-md text-sm font-medium border',
                colors.border,
                'border'
              )}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 rounded-md text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Sending…' : 'Send invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InviteUserModal;
